import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { QR_CODE_CONFIG } from '../config/qrcode.js';
import SettlementSummary from './SettlementSummary';
// 动态导入qrcode包以避免ES模块兼容性问题
let QRCode;

// 初始化QRCode
const initQRCode = async () => {
  try {
    const qrcodeModule = await import('qrcode');
    QRCode = qrcodeModule.default;
  } catch (error) {
    console.warn('qrcode包导入失败，使用备用方案:', error);
    QRCode = {
      toDataURL: async (url, options) => {
        // 备用方案：返回一个占位符图片URL，使用英文避免编码问题
        const svgContent = `<svg width="128" height="128" xmlns="http://www.w3.org/2000/svg">
          <rect width="128" height="128" fill="white"/>
          <text x="64" y="64" text-anchor="middle" dy=".3em" font-size="12" fill="black">QR Code Error</text>
        </svg>`;
        return `data:image/svg+xml;base64,${btoa(svgContent)}`;
      }
    };
  }
};

// 立即初始化
initQRCode();

// 下单/支付弹窗组件
export default function OrderModal({
  open,
  order,
  onClose,
  onRetry,
  qrcodeUrls,
  onGetWechatQrcode,
  onGetAlipayIframe,
  onPayStatus
}) {
  // 统一二维码尺寸常量
  const QR_SIZE = 256; // 统一二维码边长

  const [status, setStatus] = useState('pending');
  const [timeLeft, setTimeLeft] = useState(120);
  const [localQrcodeUrls, setLocalQrcodeUrls] = useState({});
  const [qrCodeImages, setQrCodeImages] = useState({}); // 存储生成的二维码图片

  // 支付宝前置模式状态
  const [alipayMode, setAlipayMode] = useState(null);
  const [alipayUrl, setAlipayUrl] = useState('');
  const [alipayLoading, setAlipayLoading] = useState(true);
  // 仅显示一个二维码，但生成逻辑并行进行
  const [payMethod, setPayMethod] = useState('wechat'); // 'wechat' | 'alipay'

  // 使用统一的二维码尺寸常量

  // 目标黑色内容区尺寸
  const QR_TARGET = 256;

  // 微信二维码显示尺寸状态
  const [wechatDisplaySize, setWechatDisplaySize] = useState(QR_TARGET);

  // 估算 quiet zone（每侧白边像素）——用中线扫描第一个非白像素
  function estimateQuietZonePx(img) {
    const S = Math.min(img.naturalWidth, img.naturalHeight);
    const cvs = document.createElement('canvas');
    cvs.width = cvs.height = S;
    const ctx = cvs.getContext('2d');
    ctx.drawImage(img, 0, 0, S, S);

    const y = Math.floor(S / 2);
    const data = ctx.getImageData(0, 0, S, S).data;

    // 简单判白阈值
    const isWhite = (r, g, b) => (r > 240 && g > 240 && b > 240);

    let qLeft = 0;
    for (let x = 0; x < S; x++) {
      const i = (y * S + x) * 4;
      if (!isWhite(data[i], data[i + 1], data[i + 2])) { qLeft = x; break; }
    }
    // 可选：再扫右侧提高稳健性
    let qRight = 0;
    for (let x = S - 1; x >= 0; x--) {
      const i = (y * S + x) * 4;
      if (!isWhite(data[i], data[i + 1], data[i + 2])) { qRight = S - 1 - x; break; }
    }
    // 取左右平均（避免轻微偏差）
    const q = Math.round((qLeft + qRight) / 2);
    return q;
  }

  // 根据 quiet zone 计算显示尺寸（放大后的整图尺寸）
  function computeDisplaySize(S, q, target = QR_TARGET) {
    // 防御：q 过大或异常时回退到等比显示
    if (!S || !Number.isFinite(q) || q <= 0 || 2 * q >= S) return target;
    return Math.round((target * S) / (S - 2 * q));
  }

  const navigate = useNavigate();

  // 生成二维码图片
  const generateQRCode = useCallback(async (url, type) => {
    try {
      // 直接使用接口返回的qrCode原文作为二维码内容，不再拼接/短链/编码/截断
      const qrCodeContent = url;

      if (!qrCodeContent || qrCodeContent.trim() === '') {
        console.error('OrderModal - 二维码内容为空:', type);
        return;
      }

      // 检查QRCode是否已初始化
      if (!QRCode || !QRCode.toDataURL) {
        console.warn('OrderModal - QRCode未初始化，等待初始化完成...');
        // 等待初始化完成
        await new Promise(resolve => {
          const checkInterval = setInterval(() => {
            if (QRCode && QRCode.toDataURL) {
              clearInterval(checkInterval);
              resolve();
            }
          }, 100);
          // 最多等待5秒
          setTimeout(() => {
            clearInterval(checkInterval);
            resolve();
          }, 5000);
        });
      }

      if (!QRCode || !QRCode.toDataURL) {
        throw new Error('QRCode初始化失败');
      }

      // 使用qrcode库生成二维码图片
      const qrCodeDataUrl = await QRCode.toDataURL(qrCodeContent, {
        width: QR_SIZE,
        height: QR_SIZE,
        margin: QR_CODE_CONFIG.QR_CODE_SIZE.MARGIN,
        color: {
          dark: type === 'alipay' ? QR_CODE_CONFIG.COLORS.ALIPAY.DARK : QR_CODE_CONFIG.COLORS.WECHAT.DARK,
          light: type === 'alipay' ? QR_CODE_CONFIG.COLORS.ALIPAY.LIGHT : QR_CODE_CONFIG.COLORS.WECHAT.LIGHT
        }
      });

      // 更新对应的二维码图片状态
      setQrCodeImages(prev => ({
        ...prev,
        [type]: qrCodeDataUrl
      }));

    } catch (error) {
      console.error('OrderModal - 二维码生成失败:', type, error);
      // 二维码生成失败时，不触发重新获取请求，只记录错误
    }
  }, []);

  // 监听二维码URL变化，自动生成二维码图片（相同URL不重复生成）
  useEffect(() => {
    if (!localQrcodeUrls.wechat) return;
    if (lastWechatUrlRef.current === localQrcodeUrls.wechat) {
      return; // 相同URL不重复生成，避免“刷新”
    }
    lastWechatUrlRef.current = localQrcodeUrls.wechat;
    const timeoutId = setTimeout(() => {
      generateQRCode(localQrcodeUrls.wechat, 'wechat');
    }, 200);
    return () => clearTimeout(timeoutId);
  }, [localQrcodeUrls.wechat, generateQRCode]);

  // 监听微信二维码图片变化，计算智能裁剪尺寸
  useEffect(() => {
    if (!qrCodeImages.wechat) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const S = Math.min(img.naturalWidth, img.naturalHeight);
      // 自动估算 quiet zone（若不想扫描，可写死一个经验值，如 14~18）
      const q = estimateQuietZonePx(img);
      const D = computeDisplaySize(S, q, QR_TARGET);
      setWechatDisplaySize(D);
    };
    img.src = qrCodeImages.wechat;
  }, [qrCodeImages.wechat]);

  // 支付宝处理 - 识别前置模式并设置状态（放宽条件，只要有 alipayUrl 就渲染 iframe）
  useEffect(() => {
    const iframeUrl = localQrcodeUrls?.alipayUrl;
    const mode = localQrcodeUrls?.alipayMode;

    if (iframeUrl) {
      setAlipayUrl(iframeUrl);
      setAlipayLoading(true);
      if (mode !== 'PAGE_PAY_IFRAME') {
        setAlipayMode('PAGE_PAY_IFRAME');
      } else {
        setAlipayMode(mode);
      }
      return; // 有 iframeUrl 时，不再走二维码图片分支
    }
  }, [localQrcodeUrls, generateQRCode]);

  // 调试：组件挂载和卸载
  useEffect(() => {
    return () => {
    };
  }, []);

  // 调试：props变化 - 移除不必要的依赖
  useEffect(() => {
  }, [open, order]); // 移除qrcodeUrls依赖，避免无限循环

  // 监听qrcodeUrls变化，同步到localQrcodeUrls
  useEffect(() => {
    if (qrcodeUrls) {
      setLocalQrcodeUrls(prev => {
        // 避免不必要的更新，只有当实际内容变化时才更新
        const hasChanges = Object.keys(qrcodeUrls).some(key =>
          prev[key] !== qrcodeUrls[key]
        );

        if (!hasChanges) {
          return prev;
        }

        const newUrls = { ...prev, ...qrcodeUrls };
        return newUrls;
      });
    }
  }, [qrcodeUrls]); // 移除localQrcodeUrls依赖，避免无限循环

  // 防重复调用缓存
  const qrcodeCallCache = useRef(new Map());
  // 记录最近一次生成的微信二维码URL，避免相同URL重复生成导致“刷新感”
  const lastWechatUrlRef = useRef('');

  // 打开弹窗时，初始化二维码和倒计时（关着时不做任何工作）
  useEffect(() => {
    if (!open || !order) {
      return;
    }

    setStatus('pending');
    setTimeLeft(120);

    // 重置支付宝状态
    setAlipayMode(null);
    setAlipayUrl('');

    let alipayTimer;
    let wechatTimer;

    // 自动获取支付宝 iframe
    if (onGetAlipayIframe) {
      alipayTimer = setTimeout(() => {
        onGetAlipayIframe();
      }, 100);
    }

    // 自动生成微信二维码：仅在没有现有二维码时；5 秒内同一订单只调用一次
    if (onGetWechatQrcode && !qrcodeUrls?.wechat) {
      const orderId = order.id;
      const now = Date.now();
      const lastCallTime = qrcodeCallCache.current.get(orderId) || 0;
      if (now - lastCallTime > 5000) {
        qrcodeCallCache.current.set(orderId, now);
        wechatTimer = setTimeout(() => {
          onGetWechatQrcode();
        }, 200);
      }
    }

    return () => {
      if (alipayTimer) clearTimeout(alipayTimer);
      if (wechatTimer) clearTimeout(wechatTimer);
    };
  }, [order, open, onGetAlipayIframe, onGetWechatQrcode]);

  // 切换支付方式时的并行触发保障：
  // - 切到支付宝且尚未有 alipayUrl，则主动拉取 iframe
  // - 切到微信且尚未有 wechat qrcode，则主动拉取
  useEffect(() => {
    if (!open || !order) return;
    if (payMethod === 'alipay' && !alipayUrl && onGetAlipayIframe) {
      onGetAlipayIframe();
    }
    if (payMethod === 'wechat' && !qrcodeUrls?.wechat && onGetWechatQrcode) {
      onGetWechatQrcode();
    }
  }, [payMethod, open, order, alipayUrl, qrcodeUrls, onGetAlipayIframe, onGetWechatQrcode]);

  // 轮询支付状态
  useEffect(() => {
    if (!order || !open || status !== 'pending') return;

    // 检查订单ID是否有效
    if (!order.id || order.id === 'undefined') {
      console.warn('OrderModal - 订单ID无效，跳过支付状态检查:', order.id);
      return;
    }

    let timer;
    if (onPayStatus) {
      timer = setInterval(async () => {
        try {
          const s = await onPayStatus(order.id);
          if (s === 'success') {
            setStatus('success');
            clearInterval(timer);
          } else if (s === 'fail') {
            setStatus('fail');
            clearInterval(timer);
          }
        } catch (error) {
          console.error('OrderModal - 支付状态检查失败:', error);
          // 不设置失败状态，继续重试
        }
      }, 2000);
    }
    return () => clearInterval(timer);
  }, [order, open, onPayStatus, status]);

  // 倒计时效果
  useEffect(() => {
    if (!open || status !== 'pending') return;
    if (timeLeft <= 0) {
      setStatus('timeout');
      return;
    }
    const countdownTimer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(countdownTimer);
          setStatus('timeout');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(countdownTimer);
  }, [open, status, timeLeft]);

  // 支付成功后自动跳转
  useEffect(() => {
    if (status === 'success' && order) {
      const timer = setTimeout(() => {
        navigate(`/profile/my-orders`);
        if (onClose) onClose();
      }, 2000);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [status, order, navigate, onClose]);

  // 刷新二维码
  const handleRetry = async () => {
    try {
      setStatus('pending');
      setTimeLeft(120);

      // 清除旧的二维码图片，强制重新生成
      setQrCodeImages({});

      if (onRetry) {
        await onRetry(); // 父组件会更新qrcodeUrls
      }
    } catch (error) {
      console.log('刷新二维码失败:', error);
    }
  };

  // 获取支付宝iframe
  const handleGetAlipayIframe = async () => {
    try {
      if (onGetAlipayIframe) {
        await onGetAlipayIframe();
      } else {
        console.warn('OrderModal - onGetAlipayIframe 函数不存在');
        console.error('OrderModal - 可用的函数:', {
          onGetWechatQrcode: !!onGetWechatQrcode,
          onRetry: !!onRetry,
          onClose: !!onClose
        });
      }
    } catch (error) {
      console.error('OrderModal - 获取支付宝iframe失败:', error);
    }
  };

  // 监听qrcodeUrls变化，更新本地二维码（已在前文做过一次浅比较同步，此处移除重复同步以避免双触发）

  // 格式化时间
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 w-full sm:w-[95vw] sm:max-w-md md:max-w-lg max-h-[95vh] sm:max-h-[90vh] relative mx-2">
        <button
          onClick={onClose}
          className="absolute top-2 right-2 sm:top-4 sm:right-4 z-10 p-1.5 sm:p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
          title="关闭"
        >
          <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <div className="text-base sm:text-lg font-semibold text-gray-800 dark:text-white mb-3 sm:mb-4 pr-8">支付订单</div>
        {/* 订单商品明细 - 响应式 */}
        {order?.items && order.items.length > 0 && (
          <div className="mb-3 sm:mb-4">
            <div className="font-medium mb-1 sm:mb-2 text-sm sm:text-base text-gray-700 dark:text-gray-300">订单商品：</div>
            <div className="space-y-1 max-h-24 sm:max-h-32 overflow-y-auto">
              {order.items.map((item, idx) => (
                <div key={idx} className="flex justify-between text-xs sm:text-sm text-gray-600 dark:text-gray-400 gap-2">
                  <span className="truncate flex-1">{item.productName}</span>
                  <span className="flex-shrink-0">x{item.quantity}</span>
                  <span className="flex-shrink-0">¥{item.price?.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {(order?.settlement || order?.payAmount != null) && (
          <div className="mb-3 sm:mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">结算明细</div>
            <SettlementSummary
              settlement={order.settlement || {
                goodsOriginalAmount: order.goodsAmount ?? order.totalAmount,
                campaignDiscountTotal: order.discountAmount ?? 0,
                couponDiscount: order.couponDiscountAmount ?? 0,
                payAmount: order.payAmount ?? order.totalAmount,
                shippingFee: 0
              }}
              compact
            />
          </div>
        )}
        {status === 'pending' && (
          <>
            {/* 支付方式选择 - 响应式 */}
            <div className="mb-3 sm:mb-4 flex items-center justify-center gap-3 sm:gap-4">
              <label className="flex items-center gap-1.5 sm:gap-2 cursor-pointer p-2 sm:p-3 border-2 rounded-lg transition-colors dark:border-gray-600" 
                     style={{borderColor: payMethod === 'wechat' ? '#10b981' : 'transparent'}}>
                <input type="radio" name="paymethod" checked={payMethod === 'wechat'} onChange={() => setPayMethod('wechat')} className="w-4 h-4" />
                <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-200">微信支付</span>
              </label>
              <label className="flex items-center gap-1.5 sm:gap-2 cursor-pointer p-2 sm:p-3 border-2 rounded-lg transition-colors dark:border-gray-600"
                     style={{borderColor: payMethod === 'alipay' ? '#1890ff' : 'transparent'}}>
                <input type="radio" name="paymethod" checked={payMethod === 'alipay'} onChange={() => setPayMethod('alipay')} className="w-4 h-4" />
                <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-200">支付宝支付</span>
              </label>
            </div>

            {/* 检查是否有二维码数据 */}
            {(payMethod === 'wechat' ? !localQrcodeUrls.wechat : !alipayUrl) ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <div className="text-gray-500 text-sm">正在生成支付二维码...</div>
                <button
                  onClick={handleRetry}
                  className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  重新生成
                </button>
              </div>
            ) : (
              <div>
                <div className="flex justify-center mb-6">
                  {payMethod === 'wechat' ? (
                    <div className="grid place-items-center">
                      <div className="mb-3 overflow-hidden bg-white" style={{ width: `${wechatDisplaySize}px`, height: `${wechatDisplaySize}px` }}>
                        {localQrcodeUrls.wechat && qrCodeImages.wechat ? (
                          <img
                            src={qrCodeImages.wechat}
                            alt="微信支付二维码"
                            style={{ width: `${wechatDisplaySize}px`, height: `${wechatDisplaySize}px`, imageRendering: 'pixelated', display: 'block' }}
                            draggable={false}
                          />
                        ) : (
                          <div style={{ width: `${QR_TARGET}px`, height: `${QR_TARGET}px` }} className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                            <span className="text-sm text-gray-500">正在生成微信二维码...</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center justify-center text-sm font-semibold text-gray-600 mb-2">
                        <span className="w-3 h-3 mr-1 bg-green-500 rounded-sm"></span>
                        微信扫码支付
                      </div>
                    </div>
                  ) : (
                    <div className="grid place-items-center">
                      <div className="mb-3 overflow-hidden bg-white relative" style={{ width: `${QR_TARGET}px`, height: `${QR_TARGET}px` }}>
                        {alipayUrl ? (
                          <>
                            {/* 过渡淡入，避免先白再显现 */}
                            <iframe
                              key={alipayUrl}
                              src={alipayUrl}
                              title="alipay-pagepay"
                              width={QR_TARGET}
                              height={QR_TARGET}
                              loading="eager"
                              scrolling="no"
                              style={{ border: 'none', backgroundColor: '#ffffff', display: 'block', overflow: 'hidden', opacity: alipayLoading ? 0 : 1, transition: 'opacity 150ms ease' }}
                              referrerPolicy="no-referrer"
                              onLoad={() => setAlipayLoading(false)}
                              onError={() => { setAlipayMode('ERROR'); setAlipayLoading(false); }}
                            />
                            {alipayLoading && (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                              </div>
                            )}
                          </>
                        ) : (
                          <div style={{ width: `${QR_TARGET}px`, height: `${QR_TARGET}px` }} className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            <span className="text-sm text-gray-500">正在生成支付宝二维码...</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center justify-center text-sm font-semibold text-gray-600 mb-2">
                        <span className="w-3 h-3 mr-1 bg-blue-500 rounded-sm"></span>
                        支付宝扫码支付
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 底部信息栏 - 响应式 */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-0 text-xs sm:text-sm px-2 sm:px-6">
              <div className="flex items-center text-gray-500 dark:text-gray-400">
                <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="whitespace-nowrap">支付剩余时间: {formatTime(timeLeft)}</span>
              </div>
              <div className="text-center sm:text-right">
                <span className="text-gray-500 dark:text-gray-400">实付金额</span>
                <span className="text-red-500 dark:text-red-400 font-semibold ml-1">¥ {(order?.totalAmount || order?.total || 0).toFixed(2)}</span>
              </div>
            </div>
          </>
        )}
        {status === 'success' && (
          <div className="text-center py-6 sm:py-8">
            <div className="text-green-500 text-4xl sm:text-6xl mb-3 sm:mb-4">✓</div>
            <div className="text-green-600 dark:text-green-400 text-lg sm:text-xl font-bold mb-2">支付成功！</div>
            <div className="text-gray-500 dark:text-gray-400 text-sm">订单已确认，感谢您的购买</div>
            <div className="text-gray-400 dark:text-gray-500 text-xs mt-2">2秒后自动跳转到订单列表...</div>
          </div>
        )}
        {status === 'timeout' && (
          <div className="text-center py-6 sm:py-8">
            <div className="text-yellow-500 text-4xl sm:text-6xl mb-3 sm:mb-4">⏰</div>
            <div className="text-yellow-600 dark:text-yellow-400 text-base sm:text-xl font-bold mb-3 sm:mb-4 px-4">二维码已过期，请点击下方按钮刷新</div>
            <button
              onClick={handleRetry}
              className="bg-blue-600 text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
            >
              刷新二维码
            </button>
          </div>
        )}
      </div>
    </div>
  );
} 